import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, debounceTime, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { PagedResult } from 'src/app/shared/models/paging';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { PriceRegulationService } from 'src/app/shared/services/price-regulation.service';
import { PriceRegulation, PriceRegulationSearchEvent, PriceRegulationType } from '../price-regulation';
import { ExpandListSort } from 'ok-angular-shared/components';

interface FilterState {
  query: string | null;
  organizationId: number | null;
  type: PriceRegulationType | null;
  onlyGlobal: boolean;
  priceRegulationIdentifier: string | null;
  sort: Sort | null;
  page: PageEvent;
}

interface RefreshTriggerState {
  counter: number;
}

export enum PriceRegulationQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  type = 'type',
  onlyGlobal = 'onlyGlobal',
  priceRegulationIdentifier = 'priceRegulationIdentifier',
}

@Injectable()
export class PriceRegulationListStateService extends PagerBase {
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  filteringService = inject(FilteringService);
  priceRegulationService = inject(PriceRegulationService);

  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);

  public loading: WritableSignal<boolean> = signal<boolean>(false);
  private refreshTrigger = signal<RefreshTriggerState>({ counter: 0 });
  private refreshPriceRegulationSubject = new Subject<string>();
  public currentSort = signal<ExpandListSort<PriceRegulation>>({ column: 'name', direction: 'asc' });
  public priceRegulationsState = signal<PagedResult<PriceRegulation>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.defaultPageSize,
  });
  public focusedPriceRegulation = signal<PriceRegulation | null>(null);

  public filters: WritableSignal<FilterState> = signal<FilterState>({
    priceRegulationIdentifier: this.queryParamMap$()?.get(PriceRegulationQueryParams.priceRegulationIdentifier)
      ? decodeURIComponent(this.queryParamMap$()?.get(PriceRegulationQueryParams.priceRegulationIdentifier) ?? '')
      : null,
    query: this.queryParamMap$()?.get(PriceRegulationQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()?.get(PriceRegulationQueryParams.query) ?? '')
      : null,
    organizationId: this.queryParamMap$()?.get(PriceRegulationQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()?.get(PriceRegulationQueryParams.organizationId) ?? '', 10)
      : null,
    type: (() => {
      const typeParam = this.queryParamMap$()?.get(PriceRegulationQueryParams.type);
      return typeParam ? this.filteringService.safeCastEnum(PriceRegulationType, typeParam) : null;
    })(),
    onlyGlobal: this.queryParamMap$()?.get(PriceRegulationQueryParams.onlyGlobal) === 'true',
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
  });

  public pageSizeOptions = signal<number[]>(this.pageSizes);
  public resetAllFiltersTrigger = signal<{ reset: boolean }>({ reset: false });

  constructor() {
    super();

    effect(() => {
      const filters = this.filters();
      const queryParams: Record<PriceRegulationQueryParams, string | null> = {
        [PriceRegulationQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
        [PriceRegulationQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
        [PriceRegulationQueryParams.type]: filters.type ? filters.type.toString() : null,
        [PriceRegulationQueryParams.onlyGlobal]: filters.onlyGlobal ? 'true' : null,
        [PriceRegulationQueryParams.priceRegulationIdentifier]: filters.priceRegulationIdentifier
          ? encodeURIComponent(filters.priceRegulationIdentifier)
          : null,
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });

    this.$priceRegulations.subscribe();

    this.refreshPriceRegulationSubject
      .pipe(
        tap(identifier => this.highlightPriceRegulation(identifier)),
        switchMap(identifier =>
          this.priceRegulationService.getPriceRegulation(identifier).pipe(
            map(priceRegulation => ({ identifier, priceRegulation })),
            catchError(() => of(null))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        if (!result) {
          return;
        }

        const currentResult = this.priceRegulationsState();
        const itemIndex = currentResult.items.findIndex(item => item.identifier === result.identifier);
        if (itemIndex === -1) {
          return;
        }

        const updatedItems = [...currentResult.items];
        updatedItems[itemIndex] = {
          ...result.priceRegulation,
          highlight: currentResult.items[itemIndex].highlight,
        };
        this.priceRegulationsState.set({ ...currentResult, items: updatedItems });

        setTimeout(() => {
          this.priceRegulationsState.update(latestResult => {
            const latestIndex = latestResult.items.findIndex(item => item.identifier === result.identifier);
            if (latestIndex === -1 || !latestResult.items[latestIndex].highlight?.getValue()) {
              return latestResult;
            }

            const items = [...latestResult.items];
            items[latestIndex] = {
              ...items[latestIndex],
              highlight: new BehaviorSubject<boolean>(false),
            };
            return { ...latestResult, items };
          });
        }, 3000);
      });
  }

  $priceRegulations: Observable<PagedResult<PriceRegulation>> = combineLatest([
    toObservable(this.filters),
    toObservable(this.refreshTrigger),
  ])
    .pipe(
      debounceTime(300),
      tap(() => this.loading.set(true)),
      switchMap(([filters]) => {
        if (!filters.priceRegulationIdentifier && this.filters().priceRegulationIdentifier) {
          return of(this.priceRegulationsState());
        }

        if (filters.priceRegulationIdentifier) {
          return this.priceRegulationService.getPriceRegulation(filters.priceRegulationIdentifier).pipe(
            map(
              priceRegulation =>
                <PagedResult<PriceRegulation>>{
                  items: [priceRegulation],
                  totalCount: 1,
                  pageIndex: 0,
                  pageSize: this.defaultPageSize,
                }
            ),
            tap(regulations => {
              if (this.filters().priceRegulationIdentifier === filters.priceRegulationIdentifier) {
                this.focusedPriceRegulation.set(regulations.items[0] ?? null);
                this.priceRegulationsState.set(regulations);
              }
            }),
            catchError(() => of(this.priceRegulationsState()))
          );
        }

        const searchEvent = <PriceRegulationSearchEvent>{
          paging: filters.page,
          sorting: filters.sort,
          query: filters.query ? filters.query : null,
          organizationId: filters.organizationId,
          type: filters.type,
          onlyGlobal: filters.onlyGlobal,
        };

        return this.priceRegulationService.getPriceRegulations(searchEvent).pipe(
          tap(regulations => {
            if (!this.filters().priceRegulationIdentifier && !this.focusedPriceRegulation()) {
              this.priceRegulationsState.set(regulations);
            }
          })
        );
      })
    )
    .pipe(tap(() => this.loading.set(false)));

  public resetFilter() {
    this.filters.update(current => ({
      ...current,
      query: null,
      organizationId: null,
      type: null,
      onlyGlobal: false,
      sort: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
      priceRegulationIdentifier: null,
    }));

    this.resetAllFiltersTrigger.set({ reset: true });
    this.focusedPriceRegulation.set(null);
  }

  public filterToPriceRegulation(priceRegulation: PriceRegulation) {
    this.focusedPriceRegulation.set(priceRegulation);
    this.priceRegulationsState.set({
      items: [priceRegulation],
      totalCount: 1,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    });
    this.filters.set({
      query: null,
      organizationId: null,
      type: null,
      onlyGlobal: false,
      sort: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
      priceRegulationIdentifier: priceRegulation.identifier,
    });

    this.resetAllFiltersTrigger.set({ reset: true });
  }

  triggerUpdate() {
    this.refreshTrigger.update(current => ({ counter: current.counter + 1 }));
  }

  triggerUpdateWithHighlight(identifier: string) {
    this.refreshPriceRegulationSubject.next(identifier);
  }

  private highlightPriceRegulation(identifier: string) {
    const currentResult = this.priceRegulationsState();
    const itemIndex = currentResult.items.findIndex(item => item.identifier === identifier);
    if (itemIndex === -1) {
      return;
    }

    const items = [...currentResult.items];
    items[itemIndex] = {
      ...items[itemIndex],
      highlight: new BehaviorSubject<boolean>(true),
    };
    this.priceRegulationsState.set({ ...currentResult, items });
  }

  onSortChange(sort: ExpandListSort<PriceRegulation>) {
    this.currentSort.set(sort);

    this.filters.update(x => ({
      ...x,
      sort: {
        active: sort.column as string,
        direction: sort.direction,
      },
      page: { ...x.page, pageIndex: 0 },
    }));
  }
}
